import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, Search, Tags } from 'lucide-react';
import { toast } from 'sonner';
import { getColumnValues, getInfo, recodeColumn } from '../../utils/api';
import { useT } from '../../i18n';

// How much has to be typed before the column is asked what resembles it. Below
// this, on a column of towns or of names, nearly everything comes back and the
// list proposes nothing — and each keystroke costs a request whose price is the
// number of *distinct* values in the column.
const SUGGEST_AFTER = 3;
// The list hangs under a field inside a modal; eight lines fit without turning
// it into a second scrolling pane.
const SUGGEST_LIMIT = 8;
// Long enough that a word typed at speed costs one request, not eight.
const DEBOUNCE_MS = 250;
// The new-value field is 160px wide, which truncates 'MONTREAL' to 'MONTRE…'
// once the row count is beside it. Two variants that differ past the cut would
// read as the same proposal, which is the one thing this list must not do.
const SUGGEST_MIN_WIDTH = 264;
const SUGGEST_MAX_HEIGHT = 232;
const SUGGEST_GAP = 4;
// The values list scrolls (max-h-64) and so does the modal (max-h-[90vh]).
// Both clip; the overlay is z-50.
const SUGGEST_Z_INDEX = 60;

/**
 * A text field that proposes values the column already holds.
 *
 * The point is not to save typing. It is that recoding exists to collapse
 * variants onto one spelling, and typing that spelling by hand is exactly how a
 * fourth variant gets created. What the column answers is authoritative: each
 * proposal carries its number of rows, so the canonical spelling (412 rows) is
 * told apart from the typo nobody has noticed yet (1 row).
 */
const ValueSuggestInput = ({
  column, value, onChange, exclude = [], placeholder, testId,
}) => {
  const t = useT();
  const [fetched, setFetched] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // Where the list hangs, in viewport coordinates. It is drawn outside the two
  // scrolling boxes it sits in, so it has to be told where its field is.
  const [anchor, setAnchor] = useState(null);
  const fieldRef = useRef(null);
  // Only the newest answer may land: two needles typed quickly can come back
  // out of order, and the slower one would replace the list being read.
  const ticketRef = useRef(0);
  // What was just picked or dismissed. Asking the column about it again would
  // reopen the list on the answer the user has already chosen.
  const settledRef = useRef(null);

  useEffect(() => {
    const needle = value.trim();
    if (!column || needle.length < SUGGEST_AFTER || settledRef.current === needle) {
      // Bumping the ticket drops whatever is in flight: its answer belongs to
      // a needle nobody is typing any more.
      ticketRef.current += 1;
      setFetched((current) => (current.length === 0 ? current : []));
      setOpen(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      const ticket = ticketRef.current + 1;
      ticketRef.current = ticket;
      getColumnValues(column, needle, SUGGEST_LIMIT)
        .then((result) => {
          if (ticketRef.current !== ticket) return;
          setFetched(result.values || []);
          setHighlight(-1);
          setOpen(true);
        })
        .catch(() => {
          // A proposal that cannot be fetched is not worth interrupting anyone
          // over: the field still works, the value just gets typed in full.
          if (ticketRef.current !== ticket) return;
          setFetched([]);
          setOpen(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [column, value]);

  // Proposing the very value being rewritten is noise — and worse, accepting it
  // would build a rule that asks for nothing.
  const suggestions = fetched.filter((entry) => !exclude.includes(entry.value));
  const showing = open && suggestions.length > 0;

  const place = useCallback(() => {
    const field = fieldRef.current;
    if (!field) return;
    const rect = field.getBoundingClientRect();
    const width = Math.max(rect.width, SUGGEST_MIN_WIDTH);
    // Grow leftwards from the field's right edge — there is room that way and
    // none to the right — without leaving the window.
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    const below = window.innerHeight - rect.bottom;
    if (below < SUGGEST_MAX_HEIGHT + SUGGEST_GAP && rect.top > below) {
      // No room under the field: hang it from the field's top instead, so a
      // short list still touches the field rather than floating above it.
      setAnchor({ left, width, bottom: window.innerHeight - rect.top + SUGGEST_GAP });
    } else {
      setAnchor({ left, width, top: rect.bottom + SUGGEST_GAP });
    }
  }, []);

  // Anything that moves the field moves the list: scrolling the values pane,
  // scrolling the modal, resizing the window. `true` catches the inner
  // scrollers, whose scroll events do not bubble.
  useLayoutEffect(() => {
    if (!showing) return undefined;
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [showing, place]);

  const settle = (next) => {
    settledRef.current = next.trim();
    onChange(next);
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (event) => {
    if (!showing) return;
    if (event.key === 'Escape') {
      // Close the list and nothing else. The operation modal listens for
      // Escape on the window, and closing it here would throw away every rule
      // typed so far to dismiss a dropdown.
      event.stopPropagation();
      settledRef.current = value.trim();
      setOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => (current + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
    } else if (event.key === 'Enter' && highlight >= 0) {
      event.preventDefault();
      settle(suggestions[highlight].value);
    }
  };

  return (
    <div className="relative">
      <input
        ref={fieldRef}
        type="text"
        value={value}
        onChange={(event) => {
          settledRef.current = null;
          onChange(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onBlur={() => setOpen(false)}
        className="input h-9"
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
        role="combobox"
        aria-expanded={showing}
        aria-autocomplete="list"
      />
      {showing && anchor && createPortal((
        <ul
          className="bg-card border border-border rounded-[var(--radius)] shadow-lg overflow-y-auto"
          style={{
            position: 'fixed',
            left: anchor.left,
            width: anchor.width,
            top: anchor.top,
            bottom: anchor.bottom,
            maxHeight: SUGGEST_MAX_HEIGHT,
            zIndex: SUGGEST_Z_INDEX,
          }}
          role="listbox"
          aria-label={t('recode.suggestionsLabel')}
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((entry, index) => (
            <li key={entry.value}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                // A click blurs the field first, and the list would be gone
                // before the click landed. Preventing the default keeps focus
                // where it is, so no blur happens at all.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => settle(entry.value)}
                onMouseEnter={() => setHighlight(index)}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-baseline gap-2 ${
                  index === highlight ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
                data-testid={`${testId}-suggestion-${entry.value}`}
              >
                <span className="truncate flex-1">{entry.value}</span>
                {/* The row count is the reason to read this list, so it has
                    to stay legible on the highlight: muted foreground on the
                    accent is orange on amber. */}
                <span className={`text-xs flex-shrink-0 ${
                  index === highlight ? 'text-accent-foreground/70' : 'text-muted-foreground'
                }`}
                >
                  {t('recode.occurrences', { count: entry.count.toLocaleString() })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ), document.body)}
    </div>
  );
};

/**
 * Rewrite the values of one column, choosing them from the ones actually there.
 *
 * The difference with the plain search-and-replace is that nothing is typed
 * blind: the column answers its own distinct values with their counts, so a
 * misspelling is visible before it becomes a rule that matches nothing. Several
 * values are rewritten in one pass, and the number of cells that moved comes
 * back per value.
 *
 * Variants that share no substring — 'Montreal', 'Montréal', 'MTL' — cannot be
 * brought up by one search, so the selection is kept across searches rather
 * than cleared with the list. That is the case bulk selection exists for.
 */
const RecodeOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [column, setColumn] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState(null);
  const [edits, setEdits] = useState({});
  const [selected, setSelected] = useState([]);
  const [bulkValue, setBulkValue] = useState('');
  const [loadingValues, setLoadingValues] = useState(false);
  const [applying, setApplying] = useState(false);
  // Only the newest answer may land: two searches typed quickly can come back
  // out of order, and the slower one would overwrite the list the user is
  // already reading.
  const requestRef = useRef(0);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        const names = result.info.column_names || [];
        setColumns(names);
        if (names.length > 0) setColumn(names[0]);
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const loadValues = useCallback(async (targetColumn, needle) => {
    if (!targetColumn) return;
    const ticket = requestRef.current + 1;
    requestRef.current = ticket;
    setLoadingValues(true);
    try {
      const result = await getColumnValues(targetColumn, needle);
      if (requestRef.current === ticket) setValues(result);
    } catch (error) {
      if (requestRef.current === ticket) {
        toast.error(error.response?.data?.detail || t('recode.loadValuesFailed'));
      }
    } finally {
      if (requestRef.current === ticket) setLoadingValues(false);
    }
  }, [t]);

  useEffect(() => {
    if (!column) return undefined;
    // Typing in the search box must not fire a request per keystroke on a
    // column with a million rows.
    const timer = setTimeout(() => loadValues(column, search), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [column, search, loadValues]);

  const handleColumnChange = (next) => {
    setColumn(next);
    setSearch('');
    setValues(null);
    // The edits and the selection belong to the previous column: keeping them
    // would rewrite values in a column the user never looked at.
    setEdits({});
    setSelected([]);
    setBulkValue('');
  };

  const shown = values?.values || [];
  const shownNames = shown.map((entry) => entry.value);
  const allShownSelected = shownNames.length > 0
    && shownNames.every((name) => selected.includes(name));
  // A value selected under a previous search is still selected, and still
  // about to be rewritten. Saying how many are out of sight is the difference
  // between a shortcut and a surprise.
  const hiddenSelected = selected.filter((name) => !shownNames.includes(name)).length;

  const toggleValue = (name) => setSelected((current) => (
    current.includes(name)
      ? current.filter((entry) => entry !== name)
      : [...current, name]
  ));

  const toggleAllShown = () => setSelected((current) => (
    allShownSelected
      ? current.filter((name) => !shownNames.includes(name))
      : [...current, ...shownNames.filter((name) => !current.includes(name))]
  ));

  const applyToSelection = () => {
    if (selected.length === 0 || bulkValue.trim() === '') return;
    // Nothing hidden happens here: this fills the fields that are already on
    // screen, so what will be sent stays readable in the list.
    setEdits((current) => {
      const updated = { ...current };
      selected.forEach((name) => { updated[name] = bulkValue; });
      return updated;
    });
    toast.success(t('recode.bulkFilled', { count: selected.length, value: bulkValue }));
    setSelected([]);
    setBulkValue('');
  };

  const pending = Object.entries(edits)
    // A value rewritten to itself asks for nothing. Bulk selection produces
    // these on purpose — picking the canonical spelling along with its variants
    // is the natural gesture — so they are dropped here rather than sent and
    // counted.
    .filter(([value, newValue]) => newValue !== undefined && newValue !== '' && newValue !== value)
    .map(([value, newValue]) => ({ value, new_value: newValue }));

  const handleApply = async () => {
    if (pending.length === 0) {
      toast.error(t('recode.nothingToDo'));
      return;
    }
    setApplying(true);
    try {
      const result = await recodeColumn({ column, replacements: pending });
      (result.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      // The count is the point of this operation, so it is said in the user's
      // language rather than in the server's English.
      onSuccess({
        ...result,
        localizedMessage: t('recode.done', {
          cells: (result.report?.total_cells ?? 0).toLocaleString(),
          values: pending.length,
        }),
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('recode.failed'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 text-muted-foreground">
        <Tags className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <p className="text-sm">{t('recode.intro')}</p>
      </div>

      <div>
        <label className="label mb-1">{t('form.column')}</label>
        <select
          value={column}
          onChange={(e) => handleColumnChange(e.target.value)}
          className="select"
          data-testid="recode-column"
        >
          {columns.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="label mb-1">{t('recode.searchLabel')}</label>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-9"
            placeholder={t('recode.searchPlaceholder')}
            data-testid="recode-search"
          />
        </div>
      </div>

      {selected.length > 0 && (
        <div
          className="border border-border rounded-[var(--radius)] p-3 bg-muted/40 space-y-2"
          data-testid="recode-bulk"
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium">
              {t('recode.selectedCount', { count: selected.length })}
              {hiddenSelected > 0 && (
                <span className="text-xs text-muted-foreground font-normal ml-2">
                  {t('recode.selectedHidden', { count: hiddenSelected })}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setSelected([])}
              className="text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="recode-clear-selection"
            >
              {t('recode.clearSelection')}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              {/* No exclusion here, unlike a row's own field. Ticking the
                  canonical spelling along with its variants is the natural
                  gesture, and it is the canonical spelling one then wants to
                  pick — hiding the selected values would hide exactly that.
                  The rule it makes for itself asks for nothing and is dropped
                  before sending. */}
              <ValueSuggestInput
                column={column}
                value={bulkValue}
                onChange={setBulkValue}
                placeholder={t('recode.bulkPlaceholder')}
                testId="recode-bulk-value"
              />
            </div>
            <button
              type="button"
              onClick={applyToSelection}
              disabled={bulkValue.trim() === ''}
              className="btn-secondary h-9 flex-shrink-0"
              data-testid="recode-bulk-apply"
            >
              {t('recode.bulkApply')}
            </button>
          </div>
        </div>
      )}

      {loadingValues && !values ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('recode.loadingValues')}
        </div>
      ) : values && values.values.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          {search ? t('recode.noMatch') : t('recode.emptyColumn')}
        </p>
      ) : values ? (
        <div>
          <div className="flex items-baseline justify-between mb-2 gap-3">
            <label className="label flex items-center gap-2">
              <input
                type="checkbox"
                checked={allShownSelected}
                onChange={toggleAllShown}
                className="h-4 w-4"
                aria-label={t('recode.selectAllShown')}
                data-testid="recode-select-all"
              />
              {t('recode.valuesLabel')}
            </label>
            <span className="text-xs text-muted-foreground" data-testid="recode-count">
              {values.truncated
                ? t('recode.showingSome', {
                    shown: values.values.length,
                    matched: values.matched_count,
                  })
                : t('recode.showingAll', { count: values.values.length })}
            </span>
          </div>

          <div className="border border-border rounded-[var(--radius)] divide-y divide-border max-h-64 overflow-y-auto">
            {values.values.map((entry) => (
              <div key={entry.value} className="flex items-center gap-3 p-2">
                <input
                  type="checkbox"
                  checked={selected.includes(entry.value)}
                  onChange={() => toggleValue(entry.value)}
                  className="h-4 w-4 flex-shrink-0"
                  aria-label={t('recode.selectValue', { value: entry.value })}
                  data-testid={`recode-select-${entry.value}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate" title={entry.value}>{entry.value}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('recode.occurrences', { count: entry.count.toLocaleString() })}
                  </p>
                </div>
                <span className="text-muted-foreground text-sm flex-shrink-0">→</span>
                {/* The width lives on the wrapper, not the field: `.input` is
                    w-full, so a fixed width on the field itself loses to it and
                    the value on the left collapses to nothing. */}
                <div className="w-40 flex-shrink-0">
                  <ValueSuggestInput
                    column={column}
                    value={edits[entry.value] ?? ''}
                    onChange={(next) =>
                      setEdits((current) => ({ ...current, [entry.value]: next }))
                    }
                    exclude={[entry.value]}
                    placeholder={t('recode.newValuePlaceholder')}
                    testId={`recode-new-${entry.value}`}
                  />
                </div>
              </div>
            ))}
          </div>

          {values.null_count > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {t('recode.nullNote', { count: values.null_count.toLocaleString() })}
            </p>
          )}
        </div>
      ) : null}

      <div className="flex justify-end items-center gap-3 pt-4 border-t border-border">
        <span className="text-xs text-muted-foreground mr-auto">
          {t('recode.pending', { count: pending.length })}
        </span>
        <button onClick={onCancel} className="btn-secondary" data-testid="recode-cancel">
          {t('form.cancel')}
        </button>
        <button
          onClick={handleApply}
          disabled={applying || pending.length === 0}
          className="btn-primary flex items-center gap-2"
          data-testid="recode-apply"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('recode.apply')}
        </button>
      </div>
    </div>
  );
};

export default RecodeOperation;
